import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  useContentToken,
  useCollectionBinding,
  EditableToken,
} from '@vibesites/runtime/react';
import * as Accordion from '@radix-ui/react-accordion';

interface FaqItem {
  question?: string | null;
  answer?: string | null;
}

export function FAQSection() {
  const heading = useContentToken('faqsection.heading', {
    defaultValue: 'Frequently Asked Questions',
  });

  const faqs = useCollectionBinding<FaqItem>('faqsection.items', {
    collection: 'faqs',
  });

  return (
    <section className="bg-background py-16">
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
        <EditableToken
          path="faqsection.heading"
          as="h2"
          className="mb-10 text-center font-heading text-3xl text-foreground sm:text-4xl"
        >
          {heading}
        </EditableToken>

        {!faqs || faqs.length === 0 ? (
          <p className="text-center font-body text-muted-foreground">
            No FAQs available.
          </p>
        ) : (
          <Accordion.Root
            type="single"
            collapsible
            className="divide-y divide-border overflow-hidden rounded-lg border border-border"
          >
            {faqs.map((faq, i) => (
              <Accordion.Item key={i} value={`faq-${i}`}>
                <Accordion.Header className="flex">
                  <Accordion.Trigger className="group flex flex-1 items-center justify-between px-6 py-4 text-left font-heading text-base font-medium text-foreground transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary">
                    <span>{faq.question}</span>
                    <svg
                      className="ml-4 h-5 w-5 flex-shrink-0 text-muted-foreground transition-transform duration-200 group-data-[state=open]:rotate-180"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={2}
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="m19 9-7 7-7-7"
                      />
                    </svg>
                  </Accordion.Trigger>
                </Accordion.Header>
                <Accordion.Content className="overflow-hidden px-6 pb-5 pt-1 font-body text-sm leading-relaxed text-muted-foreground">
                  {faq.answer}
                </Accordion.Content>
              </Accordion.Item>
            ))}
          </Accordion.Root>
        )}
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FAQSection',
  displayName: 'FAQs',
  tokens: {
    'faqsection.heading': {
      label: 'Section Heading',
      type: 'text',
      defaultValue: 'Frequently Asked Questions',
    },
  },
  collectionBindings: {
    'faqsection.items': {
      label: 'FAQ Items',
      collection: 'faqs',
      description: 'Frequently asked questions displayed as an expandable accordion.',
    },
  },
};

(FAQSection as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default FAQSection;
